import { useEffect, useState } from 'react'
import { MessageSquareWarning, ThumbsUp } from 'lucide-react'
import { serviceApi } from '../../services/api'
import { Avatar } from '../patterns/Avatar'

interface Ranked {
  ref: string
  receivedWeight: number
  count: number
  name: string
  avatarUrl: string | null
  deptName: string | null
  position: string | null
}

interface FeedbackData {
  criticism: Ranked[]
  praise: Ranked[]
}

const LIST_CAP = 8

function RankedList({
  items,
  loading,
  mode,
  onOpen,
}: {
  items: Ranked[]
  loading: boolean
  mode: 'criticism' | 'praise'
  onOpen?: (ref: string, name: string) => void
}) {
  if (loading) return <p className="py-2 text-sm text-muted-foreground">Loading…</p>
  if (items.length === 0) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        {mode === 'criticism' ? 'No criticism in range.' : 'No praise in range.'}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {items.slice(0, LIST_CAP).map((r) => (
        <button
          key={r.ref}
          type="button"
          onClick={() => onOpen?.(r.ref, r.name)}
          className="w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition duration-150"
        >
          <Avatar name={r.name} src={r.avatarUrl} className="h-6 w-6 text-[10px] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground truncate">{r.name}</p>
            {(r.deptName || r.position) && (
              <p className="text-xs text-muted-foreground truncate">
                {[r.deptName, r.position].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <span
            className={`shrink-0 text-xs font-medium tabular-nums ${
              mode === 'criticism' ? 'text-destructive' : 'text-emerald-500'
            }`}
          >
            {mode === 'criticism' ? `▼${r.count}` : `▲${r.count}`}
          </span>
        </button>
      ))}
    </div>
  )
}

export default function FeedbackRanking({ kind, onOpen }: { kind: 'employee' | 'client' | 'partner'; onOpen?: (ref: string, name: string) => void }) {
  const [data, setData] = useState<FeedbackData | null>(null)

  useEffect(() => {
    setData(null)
    serviceApi
      .feedbackRanking(`window=30&kind=${kind}`)
      .then((d: FeedbackData) => setData(d))
      .catch(() => setData({ criticism: [], praise: [] }))
  }, [kind])

  const loading = data === null

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
          <MessageSquareWarning size={16} className="text-destructive" />
          Most criticized
        </h2>
        <RankedList items={data?.criticism ?? []} loading={loading} mode="criticism" onOpen={onOpen} />
      </section>
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
          <ThumbsUp size={16} className="text-emerald-500" />
          Most praised
        </h2>
        <RankedList items={data?.praise ?? []} loading={loading} mode="praise" onOpen={onOpen} />
      </section>
    </div>
  )
}
